/**
 * Qud World Map Page
 * Interactive world map with search and population heatmaps
 */

import {
  loadRpmData,
  loadRpmBundle,
  loadTerrainDefs,
  buildWorldMap,
  checkDataAvailable,
} from '@/qud/dataLoader';
import { MapState } from '@/qud/map/MapState';
import { BlueprintRegistry } from '@/qud/ecs/BlueprintRegistry';
import { registerTerrainParts } from '@/qud/ecs/parts';
import { TerrainManager } from '@/qud/map/TerrainManager';
import { Search } from '@/ui/Search';
import { PopulationSearch } from '@/qud/populations/PopulationSearch';
import { GamepadController } from '@/input/GamepadController';
import type { PopulationData, WorldsData, ZoneTemplatesData } from '@/qud/types';

export async function renderMapPage(appElement: HTMLDivElement): Promise<void> {
  // Check if data is available
  const dataAvailable = await checkDataAvailable();
  if (!dataAvailable) {
    appElement.innerHTML = `
      <div class="error-screen">
        <h1>NO DATA FOUND</h1>
        <p>World data not available. Please run:</p>
        <pre>npm run build:data</pre>
        <p>Make sure config.json points to your Caves of Qud installation.</p>
      </div>
    `;
    return;
  }

  // Map needs fixed viewport (no scrolling)
  appElement.classList.add('full-viewport');

  // Setup UI structure
  appElement.innerHTML = `
    <div class="app-container">
      <header class="app-header">
        <h1>Project NAVIGATOR</h1>
        <div class="header-controls">
          <button id="demo-btn" class="control-btn">Demo Mode</button>
          <label class="control-label">
            <input type="checkbox" id="aa-toggle" checked />
            Pixel AA
          </label>
        </div>
      </header>

      <main class="app-main">
        <div class="map-container">
          <canvas id="map-canvas"></canvas>
        </div>

        <aside class="sidebar">
          <div class="search-panel">
            <input
              type="text"
              id="search-input"
              placeholder="Search entities..."
              disabled
            />
            <div id="search-results" class="search-results"></div>
          </div>

          <div class="detail-panel" id="detail-panel">
            <p class="detail-placeholder">Select an entity to view details</p>
          </div>
        </aside>
      </main>

      <footer class="app-footer">
        <span>Map: Use mouse to pan | Hover for location info</span>
        <span>Search: Coming soon</span>
      </footer>
    </div>
  `;

  const canvas = document.querySelector<HTMLCanvasElement>('#map-canvas');
  if (!canvas) {
    console.error('Canvas not found');
    return;
  }

  // Initialize map
  const mapState = new MapState(canvas);

  try {
    const timings: Record<string, number> = {};
    const t0 = performance.now();

    // Load all data in parallel (including sprite loader initialization)
    console.log('Loading data...');
    const [
      rpmData,
      terrainDefs,
      blueprintsResponse,
      populationsData,
      worldsData,
      zoneTemplatesData,
    ] = await Promise.all([
      loadRpmData(),
      loadTerrainDefs(),
      fetch('/data/blueprints.json').then((r) => r.json()),
      fetch('/data/populations.json').then((r) => r.json()) as Promise<PopulationData>,
      fetch('/data/worlds.json').then((r) => r.json()) as Promise<WorldsData>,
      fetch('/data/zone-templates.json').then((r) => r.json()) as Promise<ZoneTemplatesData>,
      mapState.initialize(), // Initialize sprite loader in parallel
    ]);
    timings['fetch+parse'] = performance.now() - t0;

    // Initialize blueprint registry with terrain parts
    const t1 = performance.now();
    const blueprintRegistry = new BlueprintRegistry();
    registerTerrainParts(blueprintRegistry.partRegistry);
    blueprintRegistry.loadFromData(blueprintsResponse);
    timings['blueprintRegistry'] = performance.now() - t1;

    // Build world map from lean RPM data
    const t1b = performance.now();
    const worldMap = buildWorldMap(rpmData, terrainDefs);
    timings['buildWorldMap'] = performance.now() - t1b;

    // Create terrain game objects - this populates tile.sprite and tile.spriteColors
    // from each terrain's Render part (after randomization parts run)
    const t2 = performance.now();
    const terrainManager = new TerrainManager(blueprintRegistry);
    terrainManager.initializeTerrain(worldMap);
    timings['terrainManager'] = performance.now() - t2;

    // Set map - tiles now have sprite data populated by game objects
    const t3 = performance.now();
    mapState.setWorldMap(worldMap);
    timings['setWorldMap'] = performance.now() - t3;

    // Start the main render loop
    mapState.start();

    // Create population search engine with RPM bundle for static spawn lookup
    const t4 = performance.now();
    const rpmBundle = await loadRpmBundle();
    const populationSearch = new PopulationSearch(
      populationsData,
      worldsData,
      zoneTemplatesData,
      rpmBundle
    );
    timings['populationSearch'] = performance.now() - t4;

    console.log('Page load timings:', timings);

    // Get all blueprint names for search
    const blueprintNames = blueprintRegistry.getBlueprintNames();
    console.log(`Loaded ${blueprintNames.length} blueprint names for search`);

    // Precompute tile data for population search
    // Extract terrain type and regionTier from each tile
    const tiles: Array<{ x: number; y: number; terrainType: string; regionTier?: number }> = [];
    for (let y = 0; y < worldMap.height; y++) {
      for (let x = 0; x < worldMap.width; x++) {
        const tile = worldMap.tiles[y]?.[x];
        if (tile?.blueprints.length > 0) {
          const terrainType = tile.blueprints[0];
          // Get regionTier from blueprint tags if available
          const resolved = blueprintRegistry.getResolved(terrainType);
          const regionTierTag = resolved?.tags?.['RegionTier'];
          const regionTier = regionTierTag ? parseInt(regionTierTag, 10) : undefined;
          tiles.push({
            x,
            y,
            terrainType,
            regionTier: isNaN(regionTier as number) ? undefined : regionTier,
          });
        }
      }
    }

    // Initialize search UI with heatmap callback
    const search = new Search('search-input', 'search-results', (blueprintName) => {
      console.log(`Searching for: ${blueprintName}`);

      // Calculate expected spawns across all strata (surface through deep underground)
      const t0 = performance.now();
      const expectedSpawns = populationSearch.calculateMapExpectedSpawns(
        blueprintName,
        tiles,
        { min: 10, max: 50 }, // All strata: surface (10) through deep underground
        'JoppaWorld'
      );
      const t1 = performance.now();
      console.log(
        `calculateMapExpectedSpawns: ${(t1 - t0).toFixed(1)}ms, ${expectedSpawns.size} tiles`
      );

      const t2 = performance.now();
      if (expectedSpawns.size > 0) {
        mapState.setAnnotations(blueprintName, expectedSpawns);
      } else {
        mapState.clearAnnotations();
      }
      const t3 = performance.now();
      console.log(`setAnnotations: ${(t3 - t2).toFixed(1)}ms`);
    });

    // Populate search with blueprint names
    search.setBlueprints(blueprintNames);

    // Wire up demo mode button
    const demoBtn = document.querySelector<HTMLButtonElement>('#demo-btn');
    if (demoBtn) {
      demoBtn.addEventListener('click', () => {
        if (mapState.isDemoModeActive()) {
          mapState.stopDemoMode();
          demoBtn.textContent = 'Demo Mode';
          demoBtn.classList.remove('active');
        } else {
          mapState.startDemoMode();
          demoBtn.textContent = 'Stop Demo';
          demoBtn.classList.add('active');
        }
      });

      // Auto-start demo mode if #demo hash is present
      if (window.location.hash === '#demo') {
        mapState.startDemoMode();
        demoBtn.textContent = 'Stop Demo';
        demoBtn.classList.add('active');
      }
    }

    // Wire up AA toggle
    const aaToggle = document.querySelector<HTMLInputElement>('#aa-toggle');
    if (aaToggle) {
      aaToggle.addEventListener('change', () => {
        mapState.setAAEnabled(aaToggle.checked);
      });
    }

    // Couch mode state and toggle
    let couchMode = false;
    const toggleCouchMode = () => {
      couchMode = !couchMode;
      const container = document.querySelector('.app-container');
      container?.classList.toggle('couch-mode', couchMode);
      // Trigger resize to update canvas dimensions
      mapState.resize();
    };

    // Gamepad controller for TV/couch mode navigation
    const PAN_SPEED = 500; // pixels per second at scale 1
    const ZOOM_SPEED = 2; // zoom factor per second

    const gamepadController = new GamepadController({
      onPan: (dx, dy, deltaTime) => {
        mapState.pan(dx * PAN_SPEED * deltaTime, dy * PAN_SPEED * deltaTime);
      },
      onZoom: (delta, deltaTime) => {
        mapState.zoomBy(delta * ZOOM_SPEED * deltaTime);
      },
      onToggleUI: () => {
        toggleCouchMode();
      },
      onToggleDemo: () => {
        mapState.toggleDemoMode();
        if (demoBtn) {
          if (mapState.isDemoModeActive()) {
            demoBtn.textContent = 'Stop Demo';
            demoBtn.classList.add('active');
          } else {
            demoBtn.textContent = 'Demo Mode';
            demoBtn.classList.remove('active');
          }
        }
      },
      onToggleStats: () => {
        mapState.toggleStats();
      },
      onToggleAA: () => {
        const newState = !mapState.isAAEnabled();
        mapState.setAAEnabled(newState);
        const aaToggle = document.querySelector<HTMLInputElement>('#aa-toggle');
        if (aaToggle) aaToggle.checked = newState;
      },
    });

    // Register gamepad with map state (polled in main loop) and start event listeners
    mapState.setGamepadController(gamepadController);
    gamepadController.start();

    // Update footer
    const footer = document.querySelector('.app-footer');
    if (footer) {
      footer.innerHTML = `
        <span>Map: Use mouse to pan | Hover for location info</span>
        <span>Search: Type to find blueprints</span>
      `;
    }
  } catch (error) {
    console.error('Failed to initialize:', error);
    appElement.innerHTML = `
      <div class="error-screen">
        <h1>ERROR</h1>
        <p>Failed to load world data:</p>
        <pre>${error instanceof Error ? error.message : String(error)}</pre>
      </div>
    `;
  }
}
